import React, { useEffect } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { Archive, Key, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Retention from "@/pages/Retention";
import ApiTokens from "@/pages/ApiTokens";

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

// Notifications / Users / Agents / Audit Log moved to their own top-level
// sidebar entries. Settings now only hosts two infra-adjacent tabs so deep
// links like `/settings?tab=retention` keep working.
const TABS = [
    { id: "retention", label: "Retention", icon: Archive, minRole: "engineer", Component: Retention },
    { id: "api-tokens", label: "API Tokens", icon: Key, minRole: "admin", Component: ApiTokens },
];

export default function Settings() {
    const { user, reload } = useAuth();
    const [params, setParams] = useSearchParams();
    const userRank = ROLE_RANK[user?.role] || 0;

    // The data-residency region pill reads from useAuth().user.tenant.region,
    // which is hydrated at login. Fetch /auth/me on mount so a recently-
    // updated region (PATCH from the superadmin console) shows up here
    // without forcing a full re-login.
    useEffect(() => {
        reload();
    }, [reload]);

    // Only surface tabs the current role can see.
    const visible = TABS.filter((t) => userRank >= ROLE_RANK[t.minRole]);
    if (visible.length === 0) {
        return <Navigate to="/" replace />;
    }

    const requested = params.get("tab") || visible[0].id;
    const active = visible.find((t) => t.id === requested) || visible[0];

    const switchTab = (id) => {
        const next = new URLSearchParams(params);
        next.set("tab", id);
        setParams(next, { replace: true });
    };

    const ActiveComponent = active.Component;

    return (
        <div className="mx-auto max-w-[1600px]">
            <div className="border-b border-border bg-surface px-6 pt-6">
                <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                            Configuration
                        </div>
                        <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Settings</h1>
                    </div>
                    {user?.tenant?.region && (
                        <div
                            data-testid="settings-region-pill"
                            className="inline-flex items-center gap-2 rounded border border-blue-400/30 bg-blue-500/[0.06] px-3 py-1.5 text-xs text-blue-200"
                            title="Data-residency region — set by your platform administrator."
                        >
                            <Globe size={13} />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-blue-300/80">
                                Region
                            </span>
                            <span className="font-semibold uppercase">{user.tenant.region}</span>
                        </div>
                    )}
                </div>
                <nav className="-mb-px flex gap-1" data-testid="settings-tabs">
                    {visible.map((t) => {
                        const Icon = t.icon;
                        const isActive = t.id === active.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => switchTab(t.id)}
                                data-testid={`settings-tab-${t.id}`}
                                className={[
                                    "inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                                    isActive
                                        ? "border-[#5b5ef0] text-[#5b5ef0]"
                                        : "border-transparent text-muted-foreground hover:border-foreground/20 hover:text-foreground",
                                ].join(" ")}
                            >
                                <Icon size={14} />
                                {t.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Render the selected tab's page. Wrap in a div so the page's own
                padding/layout still works. */}
            <div data-testid={`settings-panel-${active.id}`}>
                <ActiveComponent />
            </div>
        </div>
    );
}
